import { useCallback, useMemo, useState } from "react";
import {
  Box,
  Checkbox,
  FormControlLabel,
  Typography,
  useTheme,
} from "@mui/material";
import { useNavigate } from "react-router-dom";

import type {
  DeMinimisErrorType,
  FormContextData,
  UUID,
} from "@thalamos/aspire-common";
import {
  getBaseFormTemplate,
  getDeMinimisErrorTypes,
  getDeMinimisSelectableFields,
  getLatestVersion,
} from "@thalamos/aspire-common";

import { api } from "../../../api.js";
import { routeFns } from "../../../routePaths.js";
import {
  Banner,
  BannerList,
  Button,
  Dropdown,
  PopupDialog,
  PopupDialogTitle,
  renderErrorToast,
  renderSuccessToast,
  TextField,
} from "~/components/design-system/index.js";

/** API functions that can be injected for testing */
export interface RequestDeMinimisDialogApi {
  requestDeMinimis: (
    formId: UUID,
    body: {
      targetUserId: string;
      targetFormPart: number;
      errorType: string;
      note: string;
      selectedFields?: string[];
    },
  ) => Promise<{ status: number }>;
}

/** Default API implementation using the real API */
const defaultApi: RequestDeMinimisDialogApi = {
  requestDeMinimis: (formId, body) => api.forms.requestDeMinimis(formId, body),
};

export function RequestDeMinimisDialog({
  formId,
  formTemplateId,
  onClose,
  reloadFormContext,
  formContext,
  apiOverride,
  onSuccess,
}: {
  formContext: Omit<FormContextData, "patient">;
  formId: UUID;
  formTemplateId: string;
  onClose: () => void;
  reloadFormContext: () => void;
  /** Optional API override for testing */
  apiOverride?: RequestDeMinimisDialogApi;
  /** Optional success callback — defaults to navigating to the success dialog */
  onSuccess?: () => void;
}) {
  const apiClient = apiOverride ?? defaultApi;
  const theme = useTheme();
  const navigate = useNavigate();

  const template = getBaseFormTemplate(formTemplateId);
  const formDescription = template
    ? `Form ${template.formName}: ${template.description}`
    : formTemplateId;

  const currentForm = formContext.forms.find((f) => f.id === formId);
  const latestVersion = currentForm ? getLatestVersion(currentForm) : undefined;

  const errorTypes: DeMinimisErrorType[] = useMemo(
    () => getDeMinimisErrorTypes(formTemplateId),
    [formTemplateId],
  );
  const errorTypeOptions = errorTypes.map((et) => ({
    value: et.value,
    label: et.label,
  }));

  // Build list of signatories from signatures on the latest version
  const signatoryOptions = useMemo(() => {
    if (!latestVersion) return [];
    return latestVersion.signatures.map((sig, index) => ({
      value: JSON.stringify({
        userId: sig.userId,
        formPart: index,
      }),
      label: `Part ${String(index + 1)}: ${sig.userName}`,
    }));
  }, [latestVersion]);

  const [selectedSignatory, setSelectedSignatory] = useState<string>("");
  const [selectedErrorType, setSelectedErrorType] = useState<string>("");
  const [selectedFields, setSelectedFields] = useState<string[]>([]);
  const [note, setNote] = useState<string>("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Compute selectable fields for the chosen signatory's form part
  const selectableFields = useMemo(() => {
    if (!selectedSignatory || !template || !latestVersion) return [];
    try {
      const parsed = JSON.parse(selectedSignatory) as { formPart: number };
      const partTemplate = template.parts[parsed.formPart];
      if (!partTemplate) return [];
      const partDataWrapper = (
        latestVersion.data as { data: Record<string, unknown> }[]
      )?.[parsed.formPart];
      if (!partDataWrapper?.data) return [];
      return getDeMinimisSelectableFields(partTemplate, partDataWrapper.data);
    } catch {
      return [];
    }
  }, [selectedSignatory, template, latestVersion]);

  const canContinue =
    !isSubmitting && !!selectedSignatory && !!selectedErrorType && !!note;

  const sendDeMinimisRequest = useCallback(async () => {
    setIsSubmitting(true);
    try {
      const parsed = JSON.parse(selectedSignatory) as {
        userId: string;
        formPart: number;
      };
      const result = await apiClient.requestDeMinimis(formId, {
        targetUserId: parsed.userId,
        targetFormPart: parsed.formPart,
        errorType: selectedErrorType,
        note,
        selectedFields: selectedFields.length > 0 ? selectedFields : undefined,
      });
      if (result.status === 200) {
        renderSuccessToast({
          message: "De minimis correction request sent successfully",
        });
        reloadFormContext();
        if (onSuccess) {
          onSuccess();
        } else {
          navigate(
            routeFns.formContextPageSuccessDialog(
              formContext.id,
              formContext.patientId,
              {
                title: "De minimis correction requested",
                message:
                  "The signatory has been notified and will be asked to make the correction.",
              },
            ),
            { replace: true },
          );
        }
      }
    } catch (e: unknown) {
      const message = e instanceof Error ? e.message : "An error occurred";
      renderErrorToast({ message });
      setIsSubmitting(false);
    }
  }, [
    formId,
    selectedSignatory,
    selectedErrorType,
    selectedFields,
    note,
    formContext.id,
    formContext.patientId,
    navigate,
    reloadFormContext,
    apiClient,
    onSuccess,
  ]);

  return (
    <PopupDialog open={true} onClose={onClose} maxWidth="md">
      <PopupDialogTitle
        titleText="Request De Minimis Correction"
        closeDialog={onClose}
      />
      <Box sx={{ mb: 2 }}>
        <Banner
          body={[
            `You are requesting a de minimis (minor) correction to ${formDescription}.`,
            "De minimis corrections are for minor errors such as spelling mistakes, incorrect dates, or other small clerical errors that do not affect the legal validity of the form.",
            "The signatory will be notified and asked to review and make the correction.",
          ]}
          bannerType={BannerList.INFO}
        />
      </Box>
      <Box sx={{ mb: 2 }}>
        <Dropdown
          label="Select the signatory to request the correction from"
          name="signatory"
          values={signatoryOptions}
          selectedValue={selectedSignatory}
          onChange={(e: string | string[]) => {
            setSelectedSignatory(typeof e === "string" ? e : (e[0] ?? ""));
            setSelectedFields([]);
          }}
        />
      </Box>
      <Box sx={{ mb: 2 }}>
        <Dropdown
          label="Type of error"
          name="errorType"
          values={errorTypeOptions}
          selectedValue={selectedErrorType}
          onChange={(e: string | string[]) => {
            setSelectedErrorType(typeof e === "string" ? e : (e[0] ?? ""));
          }}
        />
      </Box>
      {selectableFields.length > 0 && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Select the field(s) that need correction (optional)
          </Typography>
          <Box
            sx={{
              border: 1,
              borderColor: "divider",
              borderRadius: 1,
              maxHeight: 300,
              overflow: "auto",
              p: 1,
            }}
          >
            {selectableFields.map((field) => (
              <Box key={field.fieldKey} sx={{ mb: 0.5 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      size="small"
                      checked={selectedFields.includes(field.fieldKey)}
                      onChange={(e) => {
                        setSelectedFields((prev) =>
                          e.target.checked
                            ? [...prev, field.fieldKey]
                            : prev.filter((k) => k !== field.fieldKey),
                        );
                      }}
                    />
                  }
                  label={
                    <Box>
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: 500,
                        }}
                      >
                        {field.label}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          color: "text.secondary",
                          fontSize: "0.8rem",
                        }}
                      >
                        {field.displayValue}
                      </Typography>
                    </Box>
                  }
                  sx={{ alignItems: "flex-start", mr: 0 }}
                />
              </Box>
            ))}
          </Box>
        </Box>
      )}
      <Box sx={{ mb: 2 }}>
        <TextField
          name="note"
          label="Please describe the correction needed"
          onChange={(e: string) => {
            setNote(e);
          }}
          value={note}
          useFullWidth={true}
          multiline
          rows="5"
        />
      </Box>
      <Box
        sx={{
          width: "100%",
          display: "flex",
          justifyContent: "space-between",
          marginTop: theme.spacing(3),
        }}
      >
        <Button label="Close" onClick={onClose} variant="outlined" />
        <Button
          color="primary"
          disabled={!canContinue}
          label="Send Request"
          onClick={() => {
            void sendDeMinimisRequest();
          }}
        />
      </Box>
    </PopupDialog>
  );
}
